/**
 * Misc helper functions
 */

import {parse} from 'query-string';
import xssFilters from 'xss-filters';
import memoize from 'lodash/memoize';
import log from 'loglevel';
import {siteTierConfig, END_CHAT_FEEDBACK_URL} from '../app.config';
import {lazy} from 'react';
import worker from 'workerize-loader!../workers/worker'; // eslint-disable-line import/no-webpack-loader-syntax
import {detect} from 'detect-browser'

let workerInstance = worker(); // `new` is optional

const isArray = (t) => t !== undefined && t !== null && t.constructor === Array;

const isNotEmptyArray = (t) => isArray(t) && t.length > 0;

const loadScriptFromUrl = (url, synch = false, callback) => {
  let s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = synch;
  s.src = url;
  let x = document.getElementsByTagName('head')[0];
  x.appendChild(s);
  s.onload = () => {
    if (callback) callback();
  };
};

const isObjectEmpty = (obj) => {
  for (var key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
};

const stringJoinWordsWithDashes = (str) => {
  if (typeof str === 'undefined') return str;
  return str.replace(/\s+/g, '-').toLowerCase();
};

const parseQueryParams = (str, params) => {
  if (!str || str === '' || !isArray(params) || params.length === 0)
    return false;

  let getParams = parse(str);

  let retObj = {};

  params.forEach(function (val) {
    if (getParams[val]) {
      retObj[val] = xssFilters.uriQueryInUnQuotedAttr(
        decodeURIComponent(getParams[val])
      );
    }
  });

  return retObj;
};

// reads string and an array of value to extract from a
// search string such as ?langauge=en_US&product=ACD
// usage memoizedParseQueryParams('?langauge=en_US&product=ACD', ['language','product'])
// return {'language': 'en_US', 'product': 'ACD'}
// empty inputs return false
const memoizedParseQueryParams = memoize(
  parseQueryParams,
  (str, params) => str + params.join('-')
);

/*
 * Checks accessibility of localStorage / sessionStorage
 * Useful in incognito/private modes for localized domains eg: .com.hk
 */
const isStorageAccessible = (getStorage) => {
  try {
    const key = '__is_storage_accessible_test_key';
    const storage = getStorage();
    storage.setItem(key, key);
    storage.removeItem(key);
    return true;
  } catch (e) {
    log.warn('Denied Access to Storage: \n%s', e);
    return false;
  }
};

const getPBCTopicId = (obj) => {
  if (obj && obj.topicIds) {
    return obj.topicIds[0];
  }
  return '';
};

const fetchLinkToMapping = (linkToKey) => {
  return new Promise(async (resolve, rejec) => {
    try {
      let data;
      const response = await workerInstance.getApiData({
        url: siteTierConfig.linkToMappingUrl,
        options: {
          timeout: 10000,
        },
      });
      data = response && response[linkToKey];
      resolve(data);
    } catch (error) {
      log.error(
        'Unable load UHM linkToMapping json',
        Object.keys(error),
        error.message
      );

      resolve();
    }
  });
};

const openEndChatFeedback = (params) => {
  const feedbackUrl = new URL(END_CHAT_FEEDBACK_URL);
  const searchParams = feedbackUrl.searchParams;
  const storage = window.sessionStorage;

  // case number and lead id are stored by ContactApp when chat starts
  if (isStorageAccessible(() => storage)) {
    // obtain any case or lead references
    const feedbackData = JSON.parse(storage.getItem('feedbackData'));
    // Extract all fields needed for qualtrics
    const {
      Team_Lead__c: sfTeamLead,
      Subject: sfSubject,
      Qualtrics_Survey_Language__c: sfQLang,
      OwnerId: sfOwnerId,
      Id: sfCaseId,
      ContactId: sfContactId,
      Center_of_Excellence__c: sfCenterOfExcellence,
      AccountId: sfAccountId,
      Owner_Team__c: sfOwnerTeam,
      Status: sfStatus,
      Owner_FirstName__c: sfFN,
      Owner_LastName__c: sfLN,
      Owner_Title__c: sfAgentTitle,
      isPartnerCase__c: sfPartner,
      Proactive_Motion__c: sfProactiveMotion,
      CaseNumber: sfCaseNumber,
    } = feedbackData;

     searchParams.append('surveySource', 'Support Chat');

    // append as query params
    if (sfTeamLead) searchParams.append('sfTeam_Lead__c', sfTeamLead);
    if (sfSubject) searchParams.append('sfSubject', sfSubject);
    if (sfQLang) searchParams.append('Q_lang', sfQLang);
    if (sfOwnerId) searchParams.append('sfOwnerId', sfOwnerId);
    if (sfCaseId) searchParams.append('sfId', sfCaseId);
    if (sfContactId) searchParams.append('sfContactId', sfContactId);
    if (sfCenterOfExcellence)
      searchParams.append('sfCenter_of_Excellence_c', sfCenterOfExcellence);
    if (sfAccountId) searchParams.append('sfAccountId', sfAccountId);
    if (sfOwnerTeam) searchParams.append('sfOwnerTeam', sfOwnerTeam);
    if (sfStatus) searchParams.append('sfStatus', sfStatus);
    if (sfFN) searchParams.append('sfFN', sfFN);
    if (sfLN) searchParams.append('sfLN', sfLN);
    if (sfAgentTitle) searchParams.append('sfAgentTitle', sfAgentTitle);
    if (sfPartner !== null) searchParams.append('sfPartner', sfPartner);
    if (sfProactiveMotion !== null)
      searchParams.append('sfProactiveMotion', sfProactiveMotion);
    searchParams.append('EmailDate', Date.now());
    if (sfCaseNumber) searchParams.append('sfCaseNumber', sfCaseNumber);

    // clear any used references
    storage.removeItem('feedbackData');
  }

  // append any optional params
  for (const [key, value] of Object.entries(params)) {
    if (value) searchParams.append(key, `${value}`);
  }

  window.open(feedbackUrl, '_blank');
};

const getOxygenReturnUrl = () => {
  let queryParms = memoizedParseQueryParams(window.location.search, [
    'uhmParentUrl',
    'parentParams',
  ]);

  let adskUhmParentUrl = queryParms.uhmParentUrl
    ? queryParms.uhmParentUrl
    : window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname.replace(/\/+$/, '');

  if (queryParms.parentParams) {
    const parentParams = queryParms.parentParams.replace(/&#61;/g, '=');
    adskUhmParentUrl = parentParams.endsWith('?')
      ? adskUhmParentUrl
      : adskUhmParentUrl + parentParams;
  }
  if (adskUhmParentUrl.endsWith('/')) {
    /*Remove the slash at the end to match the whilelist on Beehive side
     * eg: Remove slash from `https://latinoamerica.autodesk.com/`
     */
    adskUhmParentUrl = adskUhmParentUrl.slice(0, -1);
  }

  return adskUhmParentUrl;
};

/**
 * lazyWithPreload
 *
 * @param {Function} factory async import func, see:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import#dynamic_imports
 * @return {String} component that gets preloaded lazily
 */
const lazyWithPreload = (factory) => {
  const Component = lazy(factory);
  Component.preload = factory;
  return Component;
};

/**
 * handleScrollToOffsetElement
 *
 * @param {String} el element selector to scroll to
 * @param {String} scrollBehavoir optional css scroll behavior
 * @param {Function} preSelectedElement select an element to scroll to ahead of time
 */
const scrollToElement = (el, scrollBehavoir = 'smooth', preSelectedElement) => {
  const element = preSelectedElement || document.querySelector(el);

  if (element !== null) {
    element.scrollIntoView({behavior: scrollBehavoir});
  } else {
    return null;
  }
};

/**
 * getAbsoluteHeight
 *
 * @param {String} el element selector to calculate height of
 */
const getAbsoluteHeight = (el) => {
  // Get the DOM Node if you pass in a string
  el = typeof el === 'string' ? document.querySelector(el) : null;

  if (el !== null) {
    const styles = window.getComputedStyle(el);
    const margin =
      parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);

    return Math.ceil(el.offsetHeight + margin);
  } else {
    return null;
  }
};

/**
 * Function to autoscroll with every new message
 */

const scrollToBottom = (
  identifier = '[data-testid="avawrapper-container"]'
) => {
  const container = document.querySelectorAll(identifier)[0];
  if (container) {
    container.scrollTop = container.scrollHeight;
  }
};

const resizeIframeHeight = (props) => {
  const {containerDiv, parentUrl} = props;
  const containerHeight = containerDiv.offsetHeight;
  window.parent.postMessage(
    {
      type: 'UHM_RESIZE',
      isUhmOpen: true,
      containerHeight: containerHeight + 200, // Added extra height for case form buttons
    },
    parentUrl
  );
};

const scrollIframeToTop = (props) => {
  const {parentUrl} = props;
  window.parent.postMessage(
    {
      type: 'UHM_SCROLLTOP',
    },
    parentUrl
  );
}

/**
 * hasChatInPrevPage function
 * @returns {boolean} boolean
 */
 const hasChatInPrevPage = () => {
  if (!isStorageAccessible(() => sessionStorage)) {
    log.debug('Session storage not accessible so returning from this func');
    return false;
  }
  // When the user navigates away from UHM
  // while Inline chat is active we only reconnect
  // their chat if its within the below time.
  const timeToMaintainChat = 2 * 60000;
  let hasCurrentChatSession = false;
  const value = JSON.parse(sessionStorage.getItem('inlineChatEstablished'));
  const timeStamp = JSON.parse(sessionStorage.getItem('inlineChatLeavingTime'));
  const modalityType = JSON.parse(sessionStorage.getItem('inlineChatModalityType'));
  const existingUserData = JSON.parse(sessionStorage.getItem('inlineChatUserData'));
  const timeDiff = Date.now() - Number(timeStamp);
  log.debug('Time Diff in hasChatInPrevPage function: ', timeDiff);
  if (timeStamp
    && value === 'true'
    && timeDiff < timeToMaintainChat) {
    hasCurrentChatSession = true;
  }
  log.debug('hasCurrentChatSession : ',
    hasCurrentChatSession);
  return {
    hasCurrentChatSession,
    modalityType,
    existingUserData
  };
};

const shouldUseAuthFromHostPage = ({parentDomain='.com', utmSource=''}) => {
  const browser = detect();
  const browserName = browser && browser.name ? browser.name : ''
  const waitforMeMenuBrowsers = ['safari', 'firefox']
  const waitForMeMenuUTMSources = ['akn-uhm', 'estore-uhm']
  const useAuth = window.self !== window.top &&  waitForMeMenuUTMSources.includes(utmSource) && ((!parentDomain.endsWith('.com') && waitforMeMenuBrowsers.includes(browserName)) || browserName === 'safari');
return useAuth;
}

export {
  isArray,
  isNotEmptyArray,
  loadScriptFromUrl,
  isObjectEmpty,
  stringJoinWordsWithDashes,
  memoizedParseQueryParams,
  parseQueryParams,
  isStorageAccessible,
  getPBCTopicId,
  fetchLinkToMapping,
  openEndChatFeedback,
  getOxygenReturnUrl,
  lazyWithPreload,
  scrollToElement,
  getAbsoluteHeight,
  scrollToBottom,
  resizeIframeHeight,
  scrollIframeToTop,
  hasChatInPrevPage,
  shouldUseAuthFromHostPage
};
